import React from 'react';
import { Scan, PenTool, Bell, MessageCircle } from 'lucide-react';

const HowItWorks = () => {
  const steps = [
    {
      icon: Scan,
      title: "Capture",
      description: "Scan business cards, import from LinkedIn, or quickly add contacts manually. ContaX captures all the details instantly.",
      details: ["Business card scanning", "LinkedIn integration", "Manual quick-add", "Photo attachments"]
    },
    {
      icon: PenTool,
      title: "Contextualize", 
      description: "Add notes about where you met, what you discussed, and mutual interests. Build a rich profile for every connection.",
      details: ["Meeting location & event", "Conversation topics", "Shared interests", "Follow-up goals"]
    },
    {
      icon: Bell,
      title: "Remember",
      description: "Get smart reminders to follow up at the perfect time. Never let a valuable connection go cold again.",
      details: ["Intelligent timing", "Custom reminder schedules", "Priority-based alerts", "Goal-driven prompts"]
    },
    {
      icon: MessageCircle,
      title: "Connect",
      description: "Use AI-powered conversation starters and context-rich profiles to make every interaction meaningful.",
      details: ["Conversation starters", "Context-aware messaging", "Relationship tracking", "Progress monitoring"]
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            How ContaX Works
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Four simple steps to transform every networking opportunity into lasting professional relationships
          </p>
        </div>

        <div className="space-y-16">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isEven = index % 2 === 0;
            
            return (
              <div key={index} className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-12`}>
                {/* Content */}
                <div className="flex-1 text-center lg:text-left">
                  <div className="inline-flex items-center bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <span className="mr-2">Step {index + 1}</span>
                  </div>
                  
                  <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                    {step.title}
                  </h3>
                  
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                    {step.description}
                  </p>

                  <ul className="space-y-3">
                    {step.details.map((detail, detailIndex) => (
                      <li key={detailIndex} className="flex items-center text-gray-700">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mr-3 flex-shrink-0"></div>
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Visual */}
                <div className="flex-1 max-w-md">
                  <div className="relative">
                    <div className="bg-white rounded-2xl shadow-xl p-8 transform hover:scale-105 transition-transform duration-300">
                      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-6 text-white text-center mb-6">
                        <Icon className="w-12 h-12 mx-auto mb-4" />
                        <h4 className="text-lg font-semibold">{step.title}</h4>
                      </div>
                      
                      <div className="space-y-4">
                        {index === 0 && (
                          <>
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <span className="text-sm text-gray-600">Business Card</span>
                              <span className="text-blue-600 font-medium">Scanned ✓</span>
                            </div>
                            <div className="p-3 bg-blue-50 rounded-lg">
                              <div className="text-sm font-medium text-gray-900">John Smith</div>
                              <div className="text-xs text-gray-600">Senior Developer @ TechCorp</div>
                            </div>
                          </>
                        )}
                        
                        {index === 1 && (
                          <>
                            <div className="p-3 bg-gray-50 rounded-lg text-sm">
                              <strong className="text-gray-900">Met at:</strong> DevConf 2024, Coffee Booth
                            </div>
                            <div className="p-3 bg-gray-50 rounded-lg text-sm">
                              <strong className="text-gray-900">Discussed:</strong> React performance optimization
                            </div>
                            <div className="p-3 bg-blue-50 rounded-lg text-sm">
                              <strong className="text-gray-900">Follow-up:</strong> Share performance tips
                            </div>
                          </>
                        )}
                        
                        {index === 2 && (
                          <>
                            <div className="flex items-center p-3 bg-yellow-50 rounded-lg">
                              <Bell className="w-4 h-4 text-yellow-600 mr-2" />
                              <span className="text-sm text-gray-700">Follow up with John tomorrow</span>
                            </div>
                            <div className="p-3 bg-gray-50 rounded-lg text-xs text-gray-600">
                              Perfect timing based on your conversation about React optimization
                            </div>
                          </>
                        )}
                        
                        {index === 3 && (
                          <>
                            <div className="p-3 bg-green-50 rounded-lg">
                              <div className="text-sm font-medium text-gray-900">Suggested message:</div>
                              <div className="text-xs text-gray-600 mt-1">
                                "Hi John! Great meeting you at DevConf. I found that React performance article we discussed..."
                              </div>
                            </div>
                            <div className="text-center">
                              <button className="text-blue-600 text-sm font-medium">Send Message</button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;